import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, CalendarClock, FileCheck2, HeartHandshake,
  Receipt, ShieldCheck, Sparkles,
} from "lucide-react";
import EnquiryForm from "../components/EnquiryForm";

/**
 * The marketing home page (FR-TEN-07, launch support).
 *
 * The positioning is the one in docs/04 §1 — the integrated loop, not "AI
 * notes", which competitors already sell. Everything claimed here is
 * something the product actually does; the roadmap is not marketing copy.
 *
 * Deliberately not CMS-driven: this page's structure is the argument, and an
 * admin who wants to edit the argument is really writing a new page, which is
 * what the CMS is for.
 */

const BASE = import.meta.env.VITE_API_URL ?? "";

interface PublicPageSummary { slug: string; title: string; excerpt: string | null }

const LOOP = [
  {
    icon: CalendarClock,
    title: "The shift happens",
    body: "Rostered with the toggles complex care actually needs — group ratios, sleepovers, recurrence, open shifts workers claim. Clock-in captures where and when.",
  },
  {
    icon: Sparkles,
    title: "The evidence writes the note",
    body: "The case note is drafted from that shift's worklogs, attendance, and the participant's goals — context a bolt-on note-taker cannot see. A human approves it.",
  },
  {
    icon: Receipt,
    title: "The note pays the invoice",
    body: "Approving the timesheet rates it against the current NDIS Pricing Arrangements: evening and weekend bands, midnight splits, sleepover flat fees, cancellation rules, never above the price limit.",
  },
  {
    icon: FileCheck2,
    title: "The invoice becomes the claim",
    body: "Invoices batch into PACE payment requests, push to Xero, and reconcile against the remittance — the same records the audit asks for, not a re-keyed copy.",
  },
];

const DEPTH = [
  {
    icon: ShieldCheck,
    title: "Built for complex care",
    body: "Supported independent living homes with room occupancy, restrictive practices registered against the participant, incident and complaint registers, worker compliance that blocks a roster when a clearance lapses.",
  },
  {
    icon: BadgeCheck,
    title: "Audit-ready by construction",
    body: "Versioned care plans and risk assessments from your own templates, exportable as PDF. Every login, refresh, and export in an audit trail. Tenant isolation enforced in the database, not just the application.",
  },
  {
    icon: HeartHandshake,
    title: "Participants in the loop",
    body: "A portal for plan, schedule, documents and messaging, meeting WCAG. Chat and video for the team, with a retention mirror so conversations survive the vendor.",
  },
];

const PLANS = [
  { name: "Starter", limit: "Up to 50 participants", for: "A single-site provider moving off spreadsheets." },
  { name: "Professional", limit: "Up to 200 participants", for: "Multi-region providers running SIL and community supports.", featured: true },
  { name: "Enterprise", limit: "Unlimited participants", for: "Large providers who need custom roles and a migration plan." },
];

function Section({ id, children, className = "" }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`mx-auto max-w-5xl px-6 py-16 ${className}`}>
      {children}
    </section>
  );
}

export default function Marketing() {
  const [pages, setPages] = useState<PublicPageSummary[]>([]);

  useEffect(() => {
    document.title = "NDIS Provider Platform — rostering, notes, and claims in one loop";
    fetch(`${BASE}/v1/pages`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPages)
      .catch(() => setPages([]));
  }, []);

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:m-3 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>

      <header className="border-b border-edge bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/site" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-700">
              <HeartHandshake className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <span className="font-semibold text-ink">NDIS Platform</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <a href="#how" className="text-mut hover:text-body">How it works</a>
            <a href="#plans" className="hidden sm:inline text-mut hover:text-body">Plans</a>
            <Link to="/login" className="text-mut hover:text-body">Sign in</Link>
            <a href="#demo" className="btn-primary px-3 py-1.5">Request a demo</a>
          </nav>
        </div>
      </header>

      <main id="main">
        <Section className="!py-20">
          <p className="text-sm font-medium uppercase tracking-wide text-teal-700">
            For Australian NDIS providers
          </p>
          <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight text-ink sm:text-5xl">
            The shift, the note, and the claim are the same record.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-mut">
            Most systems make you run the roster in one place, write the documentation in another,
            and rebuild the claim in a third. Here the evidence a support worker generates on shift
            is what drafts the case note, prices the invoice, and files the payment request.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#demo" className="btn-primary flex items-center gap-2">
              Request a walkthrough <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <Link to="/register" className="btn-secondary">Start a trial</Link>
          </div>
        </Section>

        <div className="border-y border-edge bg-surface">
          <Section id="how">
            <h2 className="text-2xl font-semibold text-ink">One loop, four steps</h2>
            <p className="mt-2 max-w-2xl text-sm text-mut">
              Each step reads what the previous one recorded. Nothing is re-keyed, so nothing
              disagrees at audit.
            </p>
            <ol className="mt-8 grid gap-6 sm:grid-cols-2">
              {LOOP.map((step, i) => (
                <li key={step.title} className="card p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                      <step.icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <h3 className="font-medium text-ink">
                      <span className="text-faint">{i + 1}. </span>{step.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-body">{step.body}</p>
                </li>
              ))}
            </ol>
          </Section>
        </div>

        <Section>
          <h2 className="text-2xl font-semibold text-ink">Depth where the work is hard</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {DEPTH.map((item) => (
              <div key={item.title}>
                <item.icon className="h-6 w-6 text-teal-700" aria-hidden="true" />
                <h3 className="mt-3 font-medium text-ink">{item.title}</h3>
                <p className="mt-2 text-sm text-mut">{item.body}</p>
              </div>
            ))}
          </div>
        </Section>

        <div className="border-y border-edge bg-surface">
          <Section id="plans">
            <h2 className="text-2xl font-semibold text-ink">Plans</h2>
            <p className="mt-2 text-sm text-mut">
              Priced per organisation, by how many participants you support. Every plan includes
              the rostering, billing, claiming, and compliance modules — the tiers differ in scale,
              not in features.
            </p>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {PLANS.map((plan) => (
                <div key={plan.name}
                  className={`card p-6 ${plan.featured ? "ring-2 ring-teal-500" : ""}`}>
                  <h3 className="font-medium text-ink">{plan.name}</h3>
                  <p className="mt-1 text-sm text-teal-700">{plan.limit}</p>
                  <p className="mt-3 text-sm text-mut">{plan.for}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-mut">
              Talk to us for pricing — <a href="#demo" className="text-teal-700 hover:underline">request a walkthrough</a> and
              we will quote against your participant count and modules.
            </p>
          </Section>
        </div>

        <Section id="demo">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-semibold text-ink">See it on your own roster</h2>
              <p className="mt-3 text-sm text-mut">
                A walkthrough runs about 30 minutes. Bring a real week of shifts and we will show
                you what it invoices to, cap rules and all — including the parts that would be
                wrong today.
              </p>
              <p className="mt-3 text-sm text-mut">
                Already decided? <Link to="/register" className="text-teal-700 hover:underline">Register your organisation</Link> and
                the trial starts immediately.
              </p>
            </div>
            <EnquiryForm source="marketing-home" />
          </div>
        </Section>
      </main>

      <footer className="border-t border-edge bg-surface">
        <div className="mx-auto max-w-5xl px-6 py-10 text-sm">
          <div className="flex flex-wrap gap-x-10 gap-y-6">
            <div>
              <p className="font-medium text-ink">NDIS Platform</p>
              <p className="mt-1 text-mut">Operations software for Australian disability providers.</p>
            </div>
            {pages.length > 0 && (
              <nav aria-label="More pages">
                <p className="font-medium text-ink">More</p>
                <ul className="mt-1 space-y-1">
                  {pages.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/site/${p.slug}`} className="text-mut hover:text-body">{p.title}</Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
